import { useMemo } from 'react';
// Types
import { StreamStatus } from 'api/graphql-global-types';
import { GetStream_stream } from 'api/streams/types/GetStream';
import { VideoDetails } from 'components/common3/VideoDetails/VideoDetails';
// Hooks
import { useGetCurrUser } from 'hooks';
// Helpers
import {
  getStreamerName,
  getStreamerAvatar,
  getStreamSocialShareText,
  getStreamVideoDetails,
  getStreamDateV2,
  getStreamDateShortFormat,
} from 'helpers/streams';
import { parseForInnerHTML } from 'helpers/textEditor';

type UseOrderStreamInfoReturn = {
  isUserStreamCreator: boolean;
  streamerName: string;
  isStreamCanceled: boolean;
  isStreamEnded: boolean;
  socialShareButtonText: string;
  streamDateTimeText: string;
  streamDateShort: string;
  videoDetails: VideoDetails;
  profilePath: string;
  avatarURL: string;
  descriptionPreview: string;
  mobileDescriptionPreview: string;
};

export const useOrderStreamInfo = (
  stream: GetStream_stream | undefined
): UseOrderStreamInfoReturn | null => {
  const { data: userData } = useGetCurrUser();

  return useMemo(() => {
    if (!stream) {
      return null;
    }

    const { streamStatus, scheduleDateUtc, store, description } = stream;

    const isUserStreamCreator = userData?.me?.id === store?.id;
    const streamerName = getStreamerName(store);
    const isStreamCanceled = streamStatus === StreamStatus.Cancelled;
    const isStreamEnded = streamStatus === StreamStatus.Ended;
    const socialShareButtonText = getStreamSocialShareText(
      isUserStreamCreator,
      isStreamEnded,
      streamerName
    );

    const streamDatePrefix = isStreamEnded ? 'Was live on ' : '';
    const streamDateTimeText =
      streamDatePrefix + getStreamDateV2(scheduleDateUtc);
    const streamDateShort = getStreamDateShortFormat(scheduleDateUtc);

    const videoDetails = getStreamVideoDetails(stream);
    const profilePath = store?.slug ? `/${store.slug}` : '#';
    const avatarURL = getStreamerAvatar(store?.storeDetails);

    const descriptionPlainText = description
      ? parseForInnerHTML(description).replace(/<[^>]*>/g, '')
      : '';

    // Desktop: 2 lines, max 150 chars
    const descriptionLines = descriptionPlainText
      .split('\n')
      .filter((line) => line.trim())
      .slice(0, 2);
    const descriptionPreview =
      descriptionLines.length > 0
        ? descriptionLines.join(' ').substring(0, 150) +
          (descriptionLines.join(' ').length > 150 ? '...' : '')
        : '';

    // Mobile: 3 lines preview
    const mobileDescriptionLines = descriptionPlainText
      .split('\n')
      .filter((line) => line.trim())
      .slice(0, 3);
    const mobileDescriptionPreview =
      mobileDescriptionLines.length > 0 ? mobileDescriptionLines.join(' ') : '';

    return {
      isUserStreamCreator,
      streamerName,
      isStreamCanceled,
      isStreamEnded,
      socialShareButtonText,
      streamDateTimeText,
      streamDateShort,
      videoDetails,
      profilePath,
      avatarURL,
      descriptionPreview,
      mobileDescriptionPreview,
    };
  }, [stream, userData?.me?.id]);
};
